import { SubscriptionName } from 'types';

/**
 * Maximum number of voices a user can clone, by subscription plan.
 * Every plan (including Free) is allowed at least 1 cloned voice.
 */
export function getMaxClonedVoices(subscriptionName: SubscriptionName): number {
  switch (subscriptionName) {
    case 'Free':
    case 'Starter':
    case 'Unlimited GPT-3.5':
    case 'Basic':
      return 1;
    case 'Unlimited 50':
      return 2;
    case 'Unlimited':
      return 4;
    case 'Unlimited 200':
      return 6;
    case 'Unlimited 350':
      return 10;
    case 'Unlimited 500':
      return 16;
    case 'Unlimited 1000':
      return 20;
    case 'Unlimited 2000':
      return 40;
    case 'Unlimited 5000':
      return 100;
    default:
      return 2;
  }
}

/**
 * Plans allowed to use MiniMax professional voice cloning ("Unlimited 50 and
 * above", plus the business tiers that sit above it).
 *
 * Unlike ElevenLabs instant cloning — which is included in our subscription and
 * therefore open to every plan — each MiniMax clone costs $1.50 the moment it
 * is created, so it is gated to plans where that is comfortably covered.
 *
 * This is an explicit list rather than a rule derived from the plan's character
 * quota: a derived rule would silently pick up (or drop) plans as quotas are
 * tuned. Add new premium plans here.
 */
const PROFESSIONAL_VOICE_CLONE_PLANS = new Set<SubscriptionName>([
  'Unlimited 50',
  'Unlimited',
  'Unlimited 200',
  'Unlimited 350',
  'Unlimited 500',
  'Unlimited 1000',
  'Unlimited 2000',
  'Unlimited 5000',
  'Teams',
  'Teams 5 seats',
  'Teams 6 seats',
  'Teams 10 seats',
  'Teams 20 seats',
  'Teams 30 seats',
  'Teams 40 seats',
  'Teams 200',
  'Teams 300',
  'Enterprise',
]);

/**
 * Whether the plan may create MiniMax professional cloned voices. Cloned-voice
 * COUNT is still bounded by `getMaxClonedVoices` — this only controls access to
 * the more expensive cloning engine.
 */
export function canUseProfessionalVoiceClone(
  subscriptionName: SubscriptionName,
): boolean {
  return PROFESSIONAL_VOICE_CLONE_PLANS.has(subscriptionName);
}

/** Plan name shown in the upsell when professional cloning is locked. */
export const PROFESSIONAL_VOICE_CLONE_MIN_PLAN = 'Unlimited 50';

/**
 * Get the full URL for a voice image
 * @param image - Voice image path or full URL
 * @returns Full URL for the voice image
 */
export function getVoiceImageUrl(image?: string | null): string {
  // Default image if none provided
  if (!image) {
    return 'https://media.easy-peasy.ai/voices/cloned.png';
  }

  // Check if the image is already a full URL (starts with http:// or https://)
  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }

  // Otherwise, prepend the base URL
  return `https://media.easy-peasy.ai/voices/${image}`;
}
